<template>
  <div>
    <h1>Global Alerts</h1>
    <p class="text-muted">Broadcast a message to all users — including guests. Only one alert is active at a time.</p>

    <div class="card mb-4">
      <div class="card-body">
        <h5 class="card-title">{{ activeAlert ? 'Active Alert' : 'No Active Alert' }}</h5>
        <div v-if="activeAlert" class="mb-3">
          <div :class="`alert alert-${activeAlert.type} mb-2`" v-html="activeAlert.message"></div>
          <div class="small text-muted mb-2">
            Type: <strong>{{ activeAlert.type }}</strong>
            <span v-if="activeAlert.routeName"> &middot; Route: <strong>{{ activeAlert.routeName }}</strong></span>
            <span v-if="activeAlert.expiresAt"> &middot; Expires: <strong>{{ formatDate(activeAlert.expiresAt) }}</strong></span>
          </div>
          <button class="btn btn-danger btn-sm" @click="deactivate(activeAlert.id)" :disabled="saving">
            Deactivate
          </button>
        </div>
        <div v-else class="text-muted small">No alert is currently showing to users.</div>
      </div>
    </div>

    <div class="card mb-4">
      <div class="card-body">
        <h5 class="card-title">Post New Alert</h5>
        <div class="mb-3">
          <label class="form-label">Message <span class="text-muted small">(HTML allowed)</span></label>
          <textarea v-model="form.message" class="form-control" rows="3" placeholder="e.g. eBay is currently experiencing issues. Syncing may be delayed."></textarea>
        </div>
        <div class="row g-2 mb-3">
          <div class="col-auto">
            <label class="form-label">Type</label>
            <select v-model="form.type" class="form-select">
              <option value="info">Info</option>
              <option value="warning">Warning</option>
              <option value="danger">Danger</option>
            </select>
          </div>
          <div class="col-auto">
            <label class="form-label">Route Name <span class="text-muted small">(optional CTA)</span></label>
            <input v-model="form.routeName" class="form-control" placeholder="e.g. Settings-Support" />
          </div>
          <div class="col-auto">
            <label class="form-label">Expires At <span class="text-muted small">(optional)</span></label>
            <input v-model="form.expiresAt" type="datetime-local" class="form-control" />
          </div>
        </div>
        <div class="d-flex align-items-center gap-2">
          <button class="btn btn-primary" @click="post" :disabled="saving || !form.message.trim()">
            {{ saving ? 'Posting...' : 'Post Alert' }}
          </button>
          <span v-if="successMsg" class="text-success small">{{ successMsg }}</span>
          <span v-if="errorMsg" class="text-danger small">{{ errorMsg }}</span>
        </div>
      </div>
    </div>

    <div v-if="recentAlerts.length">
      <h5>Recent Alerts</h5>
      <table class="table table-sm table-striped">
        <thead>
          <tr>
            <th>Message</th>
            <th>Type</th>
            <th>Status</th>
            <th>Created</th>
            <th></th>
          </tr>
        </thead>
        <tbody>
          <tr v-for="alert in recentAlerts" :key="alert.id">
            <td class="small" v-html="alert.message"></td>
            <td><span :class="`badge bg-${alert.type}`">{{ alert.type }}</span></td>
            <td>
              <span class="badge bg-success" v-if="alert.active">Active</span>
              <span class="badge bg-secondary" v-else>Inactive</span>
            </td>
            <td class="small text-muted">{{ formatDate(alert.createdAt) }}</td>
            <td>
              <button v-if="alert.active" class="btn btn-outline-danger btn-sm" @click="deactivate(alert.id)" :disabled="saving">
                Deactivate
              </button>
            </td>
          </tr>
        </tbody>
      </table>
    </div>
  </div>
</template>

<script setup>
import { ref, computed, onMounted } from 'vue'
import { useStore } from 'vuex'

const store = useStore()
const api = computed(() => store.state.api)

const recentAlerts = ref([])
const saving = ref(false)
const successMsg = ref('')
const errorMsg = ref('')

const form = ref({
  message: '',
  type: 'info',
  routeName: '',
  expiresAt: '',
})

const activeAlert = computed(() => recentAlerts.value.find(a => a.active) || null)

async function load() {
  const data = await api.value.get('/admin/alerts').then(r => r.data)
  recentAlerts.value = data.alerts || []
}

async function post() {
  saving.value = true
  successMsg.value = ''
  errorMsg.value = ''
  try {
    await api.value.post('/admin/alerts', {
      message: form.value.message,
      type: form.value.type,
      routeName: form.value.routeName || null,
      expiresAt: form.value.expiresAt ? new Date(form.value.expiresAt).toISOString() : null,
    })
    form.value = { message: '', type: 'info', routeName: '', expiresAt: '' }
    successMsg.value = 'Alert posted.'
    await load()
  } catch (e) {
    errorMsg.value = 'Failed to post alert.'
  } finally {
    saving.value = false
  }
}

async function deactivate(id) {
  saving.value = true
  errorMsg.value = ''
  try {
    await api.value.delete(`/admin/alerts/${id}`)
    await load()
  } catch (e) {
    errorMsg.value = 'Failed to deactivate.'
  } finally {
    saving.value = false
  }
}

function formatDate(val) {
  if (!val) return '—'
  return new Date(val).toLocaleString()
}

onMounted(load)
</script>
